import Card from '../../components/Card';
import React, { useState, useEffect } from 'react';
import { useT } from '../../utils/useTranslation';
import Pagination from '../../components/Pagination';
import { DB_TYPE } from '../../api';
import { db } from '../../firebase';
import { getDocs, collection, query, where } from 'firebase/firestore';
import ajrakTexture from '../../assets/ajrak_texture_new.jpg';
import { motion } from 'framer-motion';
import HeroBackground from '../../components/ui/HeroBackground';
import { Link } from 'react-router-dom';

const ITEMS_PER_PAGE = 12;

export default function BoardPage() {
  const t = useT();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('Central');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const fetchList = async () => {
      setLoading(true);
      if (DB_TYPE === 'firebase') {
        try {
          const q = query(collection(db, 'office_bearers'), where('level', '==', filter));
          const snap = await getDocs(q);
          const bearers = snap.docs.map(d => ({ _id: d.id, ...d.data() }));
          
          if (bearers.length === 0) {
            setList([]);
            return;
          }

          // Fetch only the members we need
          const memberIds = [...new Set(bearers.map(b => b.member_id).filter(Boolean))];
          const memberMap = {};
          
          if (memberIds.length > 0) {
            const chunks = [];
            for (let i = 0; i < memberIds.length; i += 30) chunks.push(memberIds.slice(i, i + 30));

            const mSnaps = await Promise.all(chunks.map(chunk => 
              getDocs(query(collection(db, 'members'), where('__name__', 'in', chunk)))
            ));

            mSnaps.forEach(snap => {
              snap.docs.forEach(d => {
                memberMap[d.id] = { _id: d.id, ...d.data() };
              });
            });
          }

          const all = bearers.map(b => ({
            ...b,
            member: memberMap[b.member_id]
          }));

          setList(all);
        } catch (err) {
          console.error(err);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchList();
  }, [filter]);

  const isRTL = t.language === 'sindhi';

  const BoardMemberCard = ({ b }) => {
    const m = b.member || {};
    const displayName = isRTL ? (m.full_name_sindhi || m.full_name) : m.full_name;
    const displayRole = isRTL ? (t(b.role.toLowerCase().replace(/ /g, '')) || b.role) : b.role;

    return <Link to={`/board/${b._id}`} style={{ textDecoration: 'none' }}><motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ y: -10 }}
      style={{
        padding: 40,
        background: 'white',
        borderRadius: 32,
        border: '1px solid var(--sindhi-cream)',
        boxShadow: '0 15px 35px -12px rgba(0,0,0,0.08)',
        direction: isRTL ? 'rtl' : 'ltr',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        color: 'inherit'
      }}>
      <div style={{
        width: 120,
        height: 120,
        borderRadius: 40,
        background: 'var(--sindhi-ivory)',
        margin: '0 auto 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 48,
        border: '1px solid var(--sindhi-gold-muted)',
        overflow: 'hidden',
        boxShadow: '0 10px 20px -5px rgba(0,0,0,0.1)'
      }}>
        {m.photo ? <img src={m.photo} alt={displayName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : '👤'}
      </div>
      <div style={{
        fontSize: isRTL ? 28 : 22,
        fontWeight: 800,
        color: 'var(--sindhi-navy)',
        fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)',
        marginBottom: 8
      }}>
        {displayName}
      </div>
      <div style={{
        fontSize: 14,
        fontWeight: 900,
        color: 'var(--sindhi-maroon)',
        textTransform: 'uppercase',
        letterSpacing: 2,
        marginBottom: 20,
        fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit'
      }}>
        {displayRole}
      </div>
      <div style={{ fontSize: 15, color: 'var(--sindhi-text-muted)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
        {b.tenure_year && <span>📅 {b.tenure_year}</span>}
      </div>
    </motion.div></Link>;
  };

  return (
    <>
      <div className="hero-premium" style={{ minHeight: '400px', padding: '120px 2rem 100px', direction: isRTL ? 'rtl' : 'ltr' }}>
        <HeroBackground />
        <div style={{ position: 'relative', zIndex: 10, textAlign: isRTL ? 'right' : 'center', maxWidth: 1200, margin: '0 auto' }}>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-gradient-gold" style={{
            fontSize: isRTL ? 64 : 56,
            fontWeight: 800,
            marginBottom: 16,
            letterSpacing: -1,
            fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)'
          }}>
            {t('boardMembers')}
          </motion.h1>
          <p style={{
            color: 'rgba(255,255,255,0.7)',
            fontSize: 18,
            maxWidth: 600,
            margin: isRTL ? '0 0 0 auto' : '0 auto',
            fontWeight: 500,
            lineHeight: 1.6,
            fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit'
          }}>{isRTL ? 'سنڌي ادبي سنگت جي مرڪزي باڊي ۽ قيادت جيڪا تنظيم جي رهنمائي ڪري ٿي.' : 'The Central Body and leadership of Sindhi Adabi Sangat guiding the organization.'}</p>
        </div>
      </div>

      <div style={{
        background: 'var(--sindhi-ivory)',
        backgroundImage: `url(${ajrakTexture})`,
        backgroundSize: '600px 600px',
        backgroundRepeat: 'repeat',
        position: 'relative',
        zIndex: 10
      }}>
        <div style={{
          position: 'absolute',
          inset: 0,
          backdropFilter: 'blur(3px)',
          WebkitBackdropFilter: 'blur(3px)',
          background: 'rgba(253,248,235,0.8)',
          zIndex: 0
        }} />
        <div style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: '64px 24px 100px',
          position: 'relative',
          zIndex: 1,
          direction: isRTL ? 'rtl' : 'ltr'
        }}>
          {/* Level Tabs */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 48, justifyContent: 'center' }}>
            {['Central', 'District', 'Branch'].map(lvl => (
              <button 
                key={lvl} 
                onClick={() => setFilter(lvl)}
                style={{
                  padding: '12px 32px',
                  borderRadius: 16,
                  border: 'none',
                  fontSize: 14,
                  fontWeight: 800,
                  cursor: 'pointer',
                  background: filter === lvl ? 'var(--sindhi-maroon)' : 'white',
                  color: filter === lvl ? 'white' : 'var(--sindhi-navy)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit',
                  transition: 'all 0.3s'
                }}
              >
                {isRTL ? (lvl === 'Central' ? 'مرڪزي باڊي' : lvl === 'District' ? 'ضلعي عهديدار' : 'شاخ عهديدار') : lvl}
              </button>
            ))}
          </div>

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><div className="spinner" /></div>
          ) : (
            <>
              {list.length === 0 ? (
                <Card style={{ textAlign: 'center', padding: '100px 0', background: 'white' }}>
                  <div style={{ fontSize: 64, marginBottom: 24 }}>🍃</div>
                  <div style={{ fontSize: 18, color: 'var(--sindhi-text-muted)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('noMembersFound')}</div>
                </Card>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 32 }}>
                  {list.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE).map(b => (
                    <BoardMemberCard key={b._id} b={b} />
                  ))}
                </div>
              )}
              <div style={{ marginTop: 48, display: 'flex', justifyContent: 'center' }}>
                <Pagination 
                  page={page} 
                  totalPages={Math.ceil(list.length / ITEMS_PER_PAGE)} 
                  onPageChange={p => { setPage(p); window.scrollTo({ top: 400, behavior: 'smooth' }); }} 
                />
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}